import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { RagDocument } from "../api/ragApi";

interface RagDocumentRowProps {
  document: RagDocument;
  onDelete?: (docId: number) => void;
  /** If provided, shows an unlink action instead of delete (used in agent detail context) */
  onUnassign?: (docId: number) => void;
  /** Current agent ID context — used to compute "shared with N other agents" */
  currentAgentId?: number | null;
}

const RagDocumentRow = ({
  document,
  onDelete,
  onUnassign,
  currentAgentId,
}: RagDocumentRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatSize = (bytes: number | string) => {
    const b = Number(bytes);
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
    return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
  };

  // Compute other agents sharing this document (excluding current agent)
  const otherAgents = (document.agents || []).filter(
    (a) => currentAgentId && a.id !== currentAgentId,
  );
  const isShared = otherAgents.length > 0;

  const isExtracting = document.status === "processing" && document.total_chunks_expected === 0;
  const isIndexing = document.status === "processing" && document.total_chunks_expected > 0;
  const progressPercent = isIndexing
    ? Math.round((document.chunk_count / document.total_chunks_expected) * 100)
    : 0;

  return (
    <div className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="text-sm text-gray-800 truncate" title={document.filename}>
          {document.filename}
        </span>
        {/* Status badges */}
        {document.status === "error" && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-100 flex-shrink-0 cursor-default"
            title={document.error_message || t("pages.agents.error")}
          >
            {t("pages.agents.error")}
          </span>
        )}
        {isExtracting && (
          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 flex-shrink-0">
            <span className="inline-block w-3 h-3 border-[1.5px] border-yellow-500 border-t-transparent rounded-full animate-spin" />
            {t("pages.agents.extractingText")}
          </span>
        )}
        {isIndexing && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200 flex-shrink-0 tabular-nums">
            <span className="inline-block w-3 h-3 border-[1.5px] border-green-500 border-t-transparent rounded-full animate-spin" />
            {t("pages.agents.indexingChunks")} {progressPercent}%
          </span>
        )}
        <span className="text-xs text-gray-400 flex-shrink-0">
          {formatSize(document.size_bytes)}
        </span>
        {document.status === "ready" && document.chunk_count > 0 && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            {document.chunk_count} {t("pages.agents.chunks")}
          </span>
        )}

        {/* Shared indicator */}
        {isShared && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600 border border-blue-100 flex-shrink-0 cursor-default"
            title={otherAgents.map((a) => a.name).join(", ")}
          >
            {/* Link icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            +{otherAgents.length}
          </span>
        )}
      </div>

      {document.status === "error" && document.error_message && (
        <span
          className="text-xs text-red-500 mr-2 truncate max-w-[200px]"
          title={document.error_message}
        >
          {document.error_message}
        </span>
      )}

      <div className="flex items-center gap-1 flex-shrink-0 ml-auto pl-3">
        {/* View in library link */}
        {currentAgentId && (
          <button
            onClick={() => navigate("/agents/documents")}
            className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
            aria-label={t("pages.agents.viewInLibrary")}
            title={t("pages.agents.viewInLibrary")}
          >
            {/* External link icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        )}

        {/* Unassign (remove from agent) or Delete */}
        {onUnassign ? (
          <button
            onClick={() => onUnassign(document.id)}
            className="p-1 text-gray-400 hover:text-orange-500 transition-colors"
            aria-label={t("pages.agents.removeFromAgent")}
            title={t("pages.agents.removeFromAgent")}
          >
            {/* Unlink icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </button>
        ) : onDelete ? (
          <button
            onClick={() => onDelete(document.id)}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            aria-label={t("pages.agents.deleteDocument")}
            title={t("pages.agents.deleteDocument")}
          >
            {/* Trash icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default RagDocumentRow;
